import { useState } from 'react';
import { ThumbsUp, ThumbsDown, Pencil, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { submitRating, submitCorrection } from '@/services/feedback';
import { extractErrorMessage } from '@/services/api';

interface Props {
  conversationId: string | null;
  messageId: string;
  aiOutput: string;
  workbookHash?: string;
}

// FeedbackActions is the inline 👍 / 👎 / ✎ bar under each assistant
// message. State is local-only — the backend is the source of truth for
// ratings; we UPSERT on every click so flipping from 👍 to 👎 works.
export function FeedbackActions({ conversationId, messageId, aiOutput, workbookHash }: Props) {
  const [rating, setRating] = useState<0 | 1 | -1>(0);
  const [busy, setBusy] = useState(false);
  const [correcting, setCorrecting] = useState(false);

  const rate = async (value: 1 | -1) => {
    if (!conversationId) {
      toast.error('Save the conversation before rating.');
      return;
    }
    setBusy(true);
    try {
      await submitRating({
        conversation_id: conversationId,
        message_id: messageId,
        rating: value,
      });
      setRating(value);
      toast.success(value === 1 ? 'Thanks for the 👍' : 'Noted — we\'ll learn from this');
      // Negative rating signals dissatisfaction. Auto-open the correction
      // panel so the user can supply the right answer in one click instead
      // of having to find the Pencil button after rating.
      if (value === -1) {
        setCorrecting(true);
      }
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Failed to submit rating'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-1 flex items-center gap-1">
      <button
        type="button"
        disabled={busy}
        onClick={() => rate(1)}
        className={[
          'flex h-6 w-6 items-center justify-center rounded-md text-text-secondary',
          'hover:bg-black/5 hover:text-primary dark:hover:bg-white/10',
          rating === 1 ? 'text-primary bg-primary/10' : '',
        ].join(' ')}
        aria-label="Helpful"
        title="Helpful"
      >
        <ThumbsUp className="h-3.5 w-3.5" />
      </button>
      <button
        type="button"
        disabled={busy}
        onClick={() => rate(-1)}
        className={[
          'flex h-6 w-6 items-center justify-center rounded-md text-text-secondary',
          'hover:bg-black/5 hover:text-red-500 dark:hover:bg-white/10',
          rating === -1 ? 'text-red-500 bg-red-500/10' : '',
        ].join(' ')}
        aria-label="Not helpful"
        title="Not helpful"
      >
        <ThumbsDown className="h-3.5 w-3.5" />
      </button>
      <button
        type="button"
        onClick={() => setCorrecting((v) => !v)}
        className={[
          'flex h-6 items-center gap-1 rounded-md px-1.5 text-[11px] text-text-secondary',
          'hover:bg-black/5 hover:text-primary dark:hover:bg-white/10',
          correcting ? 'bg-black/5 text-primary dark:bg-white/10' : '',
        ].join(' ')}
        aria-label="Correct this answer"
        title="Correct this answer"
      >
        <Pencil className="h-3 w-3" />
        Correct
      </button>
      {correcting && (
        <CorrectionPanel
          messageId={messageId}
          aiOutput={aiOutput}
          workbookHash={workbookHash}
          onClose={() => setCorrecting(false)}
        />
      )}
    </div>
  );
}

function CorrectionPanel({
  messageId,
  aiOutput,
  workbookHash,
  onClose,
}: {
  messageId: string;
  aiOutput: string;
  workbookHash?: string;
  onClose: () => void;
}) {
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async () => {
    const trimmed = text.trim();
    if (!trimmed) {
      toast.error('Enter the correct answer.');
      return;
    }
    setSubmitting(true);
    try {
      await submitCorrection({
        message_id: messageId,
        workbook_hash: workbookHash,
        ai_output: aiOutput,
        corrected_output: trimmed,
        correction_type: 'text',
      });
      toast.success('Thanks — we\'ll use this to improve.');
      setText('');
      onClose();
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Failed to save correction'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="absolute left-0 right-0 z-20 mt-6 translate-y-0 rounded-md border border-border bg-white p-2 text-xs shadow-card dark:border-border-dark dark:bg-surface-dark">
      <div className="mb-1 flex items-center justify-between">
        <span className="font-medium">What should it have said?</span>
        <button
          type="button"
          onClick={onClose}
          className="text-text-secondary hover:text-text-primary"
          aria-label="Close"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={3}
        placeholder="What should I have done differently?"
        className="w-full resize-none rounded-md border border-border bg-transparent p-2 text-xs outline-none focus:border-primary dark:border-border-dark"
      />
      <div className="mt-1 flex justify-end gap-1">
        <button type="button" onClick={onClose} className="btn-ghost text-[11px] px-2 py-1">
          Cancel
        </button>
        <button
          type="button"
          disabled={submitting || !text.trim()}
          onClick={onSubmit}
          className="btn-primary text-[11px] px-2 py-1"
        >
          {submitting ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Save'}
        </button>
      </div>
    </div>
  );
}
